/* @flow */

/**
 * @description module for core utilities functions which doesn't have any dependency on others
 * @module utils/coreutils
 */

/**
 * @typedef {object} T
 */

/* eslint-disable no-empty-function */
/**
 * noop function
 * Utility function can be used for empty callback etc.,
 *
 * @returns {undefined}
 */
// eslint-disable-next-line no-unused-vars
export function noop(...args: Array<unknown>) {}
/* eslint-enable no-empty-function */

const objProto = Object.prototype;
// $FlowFixMe[method-unbinding]
const toStr: () => string = objProto.toString;

/**
 * Null check
 *
 * @param {T} obj  - Object to check
 * @returns {boolean} - Is given object is Null?
 */
export function isNull(obj: unknown): boolean {
  return toStr.call(obj) === '[object Null]';
}

/**
 * Undefined check
 *
 * @param {T} obj  - Object to check
 * @returns {boolean} - Is given object is undefined?
 */
export function isUndefined(obj: unknown): boolean {
  return toStr.call(obj) === '[object Undefined]';
}
/**
 * Valid object check
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it valid (Not null & not undefined)?
 */
export function isValid(obj: unknown): boolean {
  return !isNull(obj) && !isUndefined(obj);
}

/**
 * Boolean check
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - is valid oboolean object
 */
export function isBoolean(obj: unknown): boolean {
  return isValid(obj) && (obj === true || obj === false);
}

/**
 * Checks if obj is function or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a Function?
 */
export function isFunction(obj: unknown): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Function]';
}

/**
 * Checks if obj is object or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Object?
 */
export function isObject(obj: unknown): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Object]';
}

/**
 * Checks if obj is object or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Object?
 */
export function isNonEmptyObject(obj: unknown): boolean {
  if (!isObject(obj)) {
    return false;
  }
  // $FlowFixMe[not-an-object]
  return Object.keys(obj).length > 0;
}

/**
 * Checks if obj is empty object or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Object?
 */
export function isEmptyObject(obj: unknown): boolean {
  return !isNonEmptyObject(obj);
}

/**
 * Checks if obj is array or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it an Array?
 */
export function isArray(obj: unknown): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Array]';
}

/**
 * Checks if obj is string or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a String?
 */
export function isString(obj: unknown): boolean {
  return isValid(obj) && toStr.call(obj) === '[object String]';
}

/**
 * Checks if obj is number or not
 *
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it a number?
 */
export function isNumber(obj: unknown): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Number]';
}

/**
 *
 * @description Check for DOM element
 * @param {?HTMLElement} obj  Object to check
 * @returns {boolean} Check passes
 */
export function isDOMElement(obj: ?HTMLElement): boolean {
  // Ref: https://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
  // DOM Level2 and then older browsers
  return 'HTMLElement' in window
    ? Boolean(obj && obj instanceof HTMLElement)
    : Boolean(
        obj && typeof obj === 'object' && obj.nodeType === 1 && obj.nodeName
      );
}

/**
 *
 * @description Check for DOM iframe element
 * @param {?HTMLIFrameElement} obj  Object to check
 * @returns {boolean} Check passes
 */
export function isHTMLIFrameElement(
  obj: ?HTMLElement | HTMLIFrameElement
): boolean {
  // Ref: https://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
  // DOM Level2 and then older browsers
  return 'HTMLIFrameElement' in window
    ? Boolean(obj && obj instanceof HTMLIFrameElement)
    : Boolean(
        obj &&
        typeof obj === 'object' &&
        obj.nodeType === 1 &&
        obj.nodeName === 'IFRAME'
      );
}

/**
 * Removes duplicates in given array
 *
 * @param {...Array<T>} arr - Input array
 * @returns {Array<T>} - array with unique elements
 */
export function unique<T>(arr: Array<T> = []): Array<T> {
  if (!isArray(arr)) {
    return [];
  }
  return [...new Set(arr)];
}

/**
 * Find intersection between two given arrays
 *
 * @param {Array<T>} first - 1st Array
 * @param {...Array<T>} rest - rest Array
 * @returns {Array<T>} - Intersection result
 */
export function intersect<T>(
  first: Array<T> = [],
  ...rest: Array<Array<T>>
): Array<T> {
  if (!isArray(first) || rest.some(arr => !isArray(arr))) {
    return [];
  }
  // Set constructor automatically removes duplicates
  const firstSet = new Set(first);
  return rest.reduce(
    (result, currentArr) => {
      const currentSet = new Set(currentArr);
      return result.filter(v => currentSet.has(v));
    },
    [...firstSet]
  );
}

/**
 * Find difference between given arrays. Only the unique elements
 * in the first array remains
 *
 * @param {Array<T>} first - 1st Array
 * @param {...Array<T>} rest - 2nd Array
 * @returns {Array<T>} - Result with difference
 */
export function difference<T>(
  first: Array<T> = [],
  ...rest: Array<Array<T>>
): Array<T> {
  if (!isArray(first) || rest.some(arr => !isArray(arr))) {
    return [];
  }
  // Preserve duplicates in first array, only remove values that appear in rest arrays
  return rest.reduce(
    (result, currentArr) => {
      const currentSet = new Set(currentArr);
      return result.filter(v => !currentSet.has(v));
    },
    [...first]
  );
}

/**
 *
 * @param {Array<T>} arr - Array of elements to pick random one
 * @returns {T} Random element from array
 */
export function pickRandom<T>(arr: Array<T>): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * FisherYates shuffle
 * http://stackoverflow.com/a/6274398
 * https://bost.ocks.org/mike/shuffle/
 *
 * @param {Array<T>} arr - Array to be shuffled
 * @returns {Array<T>} - returns copy of shuffled array
 */
export function shuffle<T>(arr: Array<T>): Array<T> {
  // input arg check
  if (!isArray(arr)) {
    return [];
  }
  const array = [...arr];
  let counter = array.length;

  // while there are elements in the array
  // eslint-disable-next-line no-restricted-syntax
  while (counter > 0) {
    // pick a random index
    const index = Math.floor(Math.random() * counter);

    // decrease counter by 1
    counter -= 1;

    // and swap the last element with it
    const temp = array[counter];
    array[counter] = array[index];
    array[index] = temp;
  }

  return array;
}

const rbracket = /\[]$/;
/**
 * @global
 * @type {Function}
 * @callback paramAdd
 * @param {string} key
 * @param {string|function()} valOrFunction
 */
/**
 * Simplified version of PARAM serialize code from jquery
 *
 * @access private
 * @param {string} prefix - Prefix string
 * @param {(object|Array<*>|string|number)} o - child object to be serialized
 * @param {paramAdd} add - Callback fn
 * @returns {undefined}
 */
function buildParams(
  prefix: string,
  o: unknown,
  add: (string, string | (() => string)) => void
) {
  if (isObject(o)) {
    // $FlowFixMe[not-an-object]
    Object.entries(o).forEach(([k, v]) => {
      buildParams(`${prefix}[${k}]`, v, add);
    });
  } else if (isArray(o)) {
    // $FlowFixMe[incompatible-use]
    o.forEach((v: unknown, i: number) => {
      if (rbracket.test(prefix)) {
        // $FlowFixMe[incompatible-type]
        add(prefix, v);
      } else {
        buildParams(
          `${prefix}[${typeof v === 'object' && v !== null ? i : ''}]`,
          v,
          add
        );
      }
    });
  } else {
    // $FlowFixMe[incompatible-type]
    add(prefix, o ?? '');
  }
}

/**
 * Utility funciton for building jsonp params
 *
 * @param {object} a - parameters
 * @returns {string} - Serialized param
 */
export function param(a: { ... } = {}): string {
  const s = [];
  const enc = encodeURIComponent;
  const add = (key: string, valueOrFunction: string | (() => string)) => {
    let value: string = '';
    if (isFunction(valueOrFunction)) {
      // $FlowFixMe[not-a-function]
      value = valueOrFunction();
    } else {
      // $FlowFixMe[incompatible-type]
      value = valueOrFunction;
    }
    s[s.length] = `${enc(key)}=${enc(String(value))}`;
  };
  if (isObject(a)) {
    Object.entries(a).forEach(([k, v]) => {
      buildParams(k, v, add);
    });
  }
  return s.join('&');
}

/**
 * @description Helper to build URL with query string
 * @param {string} url URL
 * @param {object}  p Query param key 7 values
 * @returns {string} Built URL
 */
export function buildUrl(url: string = '', p: { ... } = {}): string {
  let u = url;
  if (u.length === 0) {
    return u;
  }
  const paramString = param(p);
  if (paramString.length < 1) {
    return u;
  }
  // check and add
  // serialize data for adding to query param
  if (!u.includes('?')) {
    u += '?';
  }
  // if it doesn't end with ? or &, add &
  // eslint-disable-next-line unicorn/prefer-at
  if (u[u.length - 1] !== '&' && u[u.length - 1] !== '?') {
    u += '&';
  }
  u += paramString;
  return u;
}

/**
 * Convert cents to dollar
 *
 * @param {number} c - Cents to be converted
 * @returns {string} - Dollar value
 */
export function centsToDollar(c: number = 0): string {
  if (!isNumber(c)) {
    return '0.00';
  }
  return (c / 100).toFixed(2);
}

// flowlint unclear-type:off
/* eslint-disable no-restricted-syntax */
/**
 * jQuery extend functionality
 * Supports variable number of arguments
 *
 * @param {boolean|object} deepArg - Boolear for deep merge OR first object
 * @param {Array<object>} args - Remainint objects to be merged
 * @returns {object} - Extended result object
 */
export function extend(deepArg: boolean | any, ...args: Array<any>): any {
  let options;
  let name;
  let src;
  let copy;
  let copyIsArray;
  let clone;
  let target: any = deepArg || {};
  let i = 0;
  let deep = false;

  // eslint-disable-next-line prefer-destructuring
  const length = args.length;
  // Handle a deep copy situation
  if (typeof target === 'boolean') {
    deep = target;
    target = args[0] || {};
    // skip the boolean and the target
    i = 1;
  }

  // Handle case when target is a string or something (possible in deep copy)
  if (typeof target !== 'object' && !isFunction(target)) {
    target = {};
  }

  for (; i < length; i += 1) {
    // Only deal with non-null/undefined values
    if ((options = args[i]) !== null) {
      // Extend the base object
      /* eslint-disable no-continue */
      for (name in options) {
        // $FlowFixMe[method-unbinding]
        if (!Object.prototype.hasOwnProperty.call(options, name)) {
          continue; // ignore inherited properties
        }
        src = target[name];
        copy = options[name];

        // Prevent never-ending loop
        if (target === copy) {
          continue;
        }
        /* eslint-enable no-continue */
        // Recurse if we're merging plain objects or arrays
        if (deep && copy && (isObject(copy) || (copyIsArray = isArray(copy)))) {
          // flowlint-next-line sketchy-null-bool:off
          if (copyIsArray) {
            copyIsArray = false;
            clone = src && isArray(src) ? src : [];
          } else {
            clone = src && isObject(src) ? src : {};
          }

          // Never move original objects, clone them
          target[name] = extend(deep, clone, copy);

          // Don't bring in undefined values
        } else if (copy !== undefined) {
          target[name] = copy;
        }
      }
    }
  }
  // Return the modified object
  return target;
}
/* eslint-enable no-restricted-syntax */
// flowlint unclear-type:error
